import React, { useEffect, useRef, useState } from 'react';
import { Modal, Form, message } from 'antd';
import { CreditCardOutlined, LoadingOutlined, ScanOutlined, CheckCircleOutlined, WifiOutlined } from '@ant-design/icons';
import { registerEmployeeRfid, triggerEsp32RfidScan } from '../../services/attendance.service';
import styled, { keyframes } from 'styled-components';

// ─── Animations ───────────────────────────────────────────────────────────────
const pulse = keyframes`
    0%, 100% { box-shadow: 0 0 0 0 rgba(79, 70, 229, 0.4); }
    50%       { box-shadow: 0 0 0 12px rgba(79, 70, 229, 0); }
`;

const spin = keyframes`
    from { transform: rotate(0deg); }
    to   { transform: rotate(360deg); }
`;

// ─── Styled components ────────────────────────────────────────────────────────
const StyledModal = styled(Modal)`
    .ant-modal-content {
        background: rgba(255, 255, 255, 0.97);
        backdrop-filter: blur(10px);
        border-radius: 18px;
        border: 1px solid rgba(220, 220, 220, 0.4);
        box-shadow: 0 16px 48px rgba(0, 0, 0, 0.10);
        padding: 0;
        overflow: hidden;
    }
    .ant-modal-header {
        background: linear-gradient(135deg, #f8f7ff 0%, #f3f2ff 100%);
        border-bottom: 1px solid rgba(79, 70, 229, 0.1);
        padding: 20px 24px 16px;
        margin-bottom: 0;
    }
    .ant-modal-title {
        font-size: 17px;
        font-weight: 700;
        color: #1a1a1a;
        display: flex;
        align-items: center;
        gap: 8px;
    }
    .ant-modal-body {
        padding: 24px;
    }
    .ant-modal-close {
        top: 18px;
    }
`;

const ScanZone = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 16px;
    padding: 28px 20px;
    border-radius: 14px;
    background: ${({ $scanning }) =>
        $scanning
            ? 'linear-gradient(135deg, #eef2ff 0%, #e0e7ff 100%)'
            : $success =>
              $success
                ? 'linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%)'
                : '#fafafa'};
    border: 2px dashed ${({ $scanning, $success }) =>
        $scanning ? '#818cf8' : $success ? '#4ade80' : '#e5e7eb'};
    transition: all 0.3s ease;
    margin-bottom: 20px;
`;

const ScanIcon = styled.div`
    width: 64px;
    height: 64px;
    border-radius: 50%;
    background: ${({ $scanning, $success }) =>
        $success
            ? 'linear-gradient(135deg, #22c55e, #16a34a)'
            : $scanning
            ? 'linear-gradient(135deg, #4f46e5, #7c3aed)'
            : 'linear-gradient(135deg, #e5e7eb, #d1d5db)'};
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 26px;
    color: white;
    transition: all 0.3s ease;
    animation: ${({ $scanning }) => ($scanning ? pulse : 'none')} 1.4s ease-in-out infinite;
`;

const CountdownRing = styled.div`
    width: 44px;
    height: 44px;
    border-radius: 50%;
    border: 3px solid #e0e7ff;
    border-top-color: #4f46e5;
    animation: ${spin} 1s linear infinite;
    display: ${({ $visible }) => ($visible ? 'block' : 'none')};
`;

const ScanButton = styled.button`
    background: ${({ $scanning }) =>
        $scanning
            ? 'linear-gradient(135deg, #6366f1, #8b5cf6)'
            : 'linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%)'};
    color: white;
    border: none;
    padding: 12px 28px;
    border-radius: 10px;
    font-size: 14px;
    font-weight: 600;
    cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};
    display: flex;
    align-items: center;
    gap: 8px;
    transition: all 0.2s ease;
    box-shadow: 0 4px 14px rgba(79, 70, 229, 0.3);
    opacity: ${({ disabled }) => (disabled ? 0.7 : 1)};

    &:hover:not(:disabled) {
        transform: translateY(-1px);
        box-shadow: 0 6px 18px rgba(79, 70, 229, 0.35);
    }
    &:active:not(:disabled) {
        transform: translateY(0);
    }
`;

const Divider = styled.div`
    display: flex;
    align-items: center;
    gap: 12px;
    margin: 16px 0;
    color: #9ca3af;
    font-size: 12px;
    font-weight: 500;

    &::before, &::after {
        content: '';
        flex: 1;
        height: 1px;
        background: #e5e7eb;
    }
`;

const ManualInput = styled.input`
    width: 100%;
    height: 44px;
    border-radius: 10px;
    border: 1.5px solid #e5e7eb;
    padding: 0 14px 0 40px;
    font-size: 14px;
    font-family: 'Consolas', 'Monaco', monospace;
    letter-spacing: 0.04em;
    color: #1a1a1a;
    outline: none;
    transition: border-color 0.2s, box-shadow 0.2s;
    background: white;
    text-transform: uppercase;
    box-sizing: border-box;

    &::placeholder {
        color: #c4c4c4;
        letter-spacing: 0;
        font-family: inherit;
        text-transform: none;
        font-size: 13px;
    }

    &:focus {
        border-color: #4f46e5;
        box-shadow: 0 0 0 3px rgba(79, 70, 229, 0.1);
    }
`;

const InputWrapper = styled.div`
    position: relative;
    margin-top: 4px;
`;

const InputIcon = styled.span`
    position: absolute;
    left: 12px;
    top: 50%;
    transform: translateY(-50%);
    color: #9ca3af;
    font-size: 16px;
    pointer-events: none;
    display: flex;
    align-items: center;
`;

const ActionButtons = styled.div`
    display: flex;
    justify-content: flex-end;
    gap: 12px;
    margin-top: 24px;
    padding-top: 16px;
    border-top: 1px solid #f3f4f6;
`;

const CancelBtn = styled.button`
    background: transparent;
    border: 1.5px solid #e0e0e0;
    color: #666;
    padding: 10px 20px;
    border-radius: 9px;
    font-size: 14px;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s;

    &:hover {
        background: #f5f5f5;
        color: #333;
    }
    &:disabled {
        opacity: 0.5;
        cursor: not-allowed;
    }
`;

const SaveBtn = styled.button`
    background: linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%);
    color: white;
    border: none;
    padding: 10px 24px;
    border-radius: 9px;
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
    box-shadow: 0 4px 12px rgba(79, 70, 229, 0.2);
    transition: all 0.2s;
    display: flex;
    align-items: center;
    gap: 6px;

    &:hover:not(:disabled) {
        transform: translateY(-1px);
        box-shadow: 0 6px 16px rgba(79, 70, 229, 0.3);
    }
    &:disabled {
        background: #d1d5db;
        box-shadow: none;
        cursor: not-allowed;
        transform: none;
    }
`;

const DeviceHint = styled.div`
    font-size: 11.5px;
    color: #6b7280;
    display: flex;
    align-items: center;
    gap: 5px;
    margin-top: 6px;
`;

// ─── Component ────────────────────────────────────────────────────────────────
const SCAN_TIMEOUT_S = 10;

const RfidRegisterModal = ({ open, onCancel, userId, userName, currentRfid, tenantId, onSuccess }) => {
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);
    const [scanning, setScanning] = useState(false);
    const [scanSuccess, setScanSuccess] = useState(false);
    const [countdown, setCountdown] = useState(0);
    const [manualUid, setManualUid] = useState('');
    const countdownRef = useRef(null);
    const esp32Ip = localStorage.getItem('code360_esp32_ip') || '';

    useEffect(() => {
        if (open) {
            setManualUid(currentRfid || '');
            setScanning(false);
            setScanSuccess(false);
            setCountdown(0);
        }
        return () => {
            if (countdownRef.current) clearInterval(countdownRef.current);
        };
    }, [open, currentRfid]);

    const startCountdown = () => {
        setCountdown(SCAN_TIMEOUT_S);
        countdownRef.current = setInterval(() => {
            setCountdown((c) => {
                if (c <= 1) {
                    clearInterval(countdownRef.current);
                    return 0;
                }
                return c - 1;
            });
        }, 1000);
    };

    const stopCountdown = () => {
        if (countdownRef.current) clearInterval(countdownRef.current);
        setCountdown(0);
    };

    const handleScan = async () => {
        if (!esp32Ip) {
            message.warning(
                'No ESP32 IP configured. Go to Settings → IoT Device Manager and save the device IP first.',
            );
            return;
        }
        setScanning(true);
        setScanSuccess(false);
        startCountdown();

        try {
            const data = await triggerEsp32RfidScan(esp32Ip);
            stopCountdown();
            if (data?.success && data?.rfid_uid) {
                const uid = data.rfid_uid.toUpperCase();
                setManualUid(uid);
                setScanSuccess(true);
                setScanning(false);
                message.success(`Card detected! UID: ${uid}`);
            } else {
                setScanning(false);
                message.error('No card detected. Try again.');
            }
        } catch (err) {
            stopCountdown();
            setScanning(false);
            if (err?.response?.status === 408 || err?.code === 'ECONNABORTED') {
                message.warning('Scan timed out. No card was swiped within 10 seconds.');
            } else {
                message.error('Could not reach the device. Ensure the ESP32 IP is correct and device is online.');
            }
        }
    };

    const handleSubmit = () => {
        const uid = manualUid.trim().toUpperCase();
        if (!uid || uid.length < 3) {
            message.warning('Please scan a card or enter the RFID UID manually.');
            return;
        }
        setLoading(true);
        registerEmployeeRfid({ userId, rfid: uid }, tenantId)
            .then(() => {
                message.success(`RFID Card assigned to ${userName} successfully!`);
                setManualUid('');
                onSuccess?.();
                onCancel();
            })
            .catch((err) => {
                message.error(err?.response?.data?.message || err?.message || 'Failed to map RFID card');
            })
            .finally(() => setLoading(false));
    };

    const scanLabel = scanning
        ? `Waiting for card... ${countdown}s`
        : scanSuccess
        ? 'Scan Another Card'
        : 'Scan Card';

    return (
        <StyledModal
            open={open}
            title={
                <>
                    <CreditCardOutlined style={{ color: '#4f46e5' }} />
                    <span>Assign RFID Card</span>
                </>
            }
            onCancel={onCancel}
            footer={null}
            destroyOnClose
            width={460}
            centered
        >
            {/* ── Scan Zone ── */}
            <ScanZone $scanning={scanning} $success={scanSuccess && !scanning}>
                <ScanIcon $scanning={scanning} $success={scanSuccess && !scanning}>
                    {scanning ? (
                        <LoadingOutlined style={{ fontSize: 28 }} />
                    ) : scanSuccess ? (
                        <CheckCircleOutlined style={{ fontSize: 28 }} />
                    ) : (
                        <ScanOutlined style={{ fontSize: 28 }} />
                    )}
                </ScanIcon>

                <div style={{ textAlign: 'center' }}>
                    {scanning ? (
                        <>
                            <div style={{ fontWeight: 700, color: '#4f46e5', fontSize: '14px' }}>
                                Ready — swipe the card now!
                            </div>
                            <div style={{ color: '#6366f1', fontSize: '12.5px', marginTop: '4px' }}>
                                Place the RFID card on the scanner. Timing out in{' '}
                                <strong>{countdown}s</strong>...
                            </div>
                        </>
                    ) : scanSuccess ? (
                        <>
                            <div style={{ fontWeight: 700, color: '#16a34a', fontSize: '14px' }}>
                                Card UID captured!
                            </div>
                            <div style={{ color: '#166534', fontSize: '12.5px', marginTop: '4px' }}>
                                UID auto-filled below. Click <strong>Save Card</strong> to register.
                            </div>
                        </>
                    ) : (
                        <>
                            <div style={{ fontWeight: 600, color: '#374151', fontSize: '13.5px' }}>
                                Click below to scan via ESP32
                            </div>
                            <div style={{ color: '#9ca3af', fontSize: '12px', marginTop: '2px' }}>
                                The device will wait 10 seconds for a card swipe
                            </div>
                        </>
                    )}
                </div>

                <ScanButton onClick={handleScan} disabled={scanning || loading} $scanning={scanning}>
                    {scanning ? <LoadingOutlined /> : <ScanOutlined />}
                    {scanLabel}
                </ScanButton>

                {esp32Ip ? (
                    <DeviceHint>
                        <WifiOutlined style={{ color: '#10b981' }} />
                        Connected to device at <strong style={{ marginLeft: 3 }}>{esp32Ip}</strong>
                    </DeviceHint>
                ) : (
                    <DeviceHint style={{ color: '#f59e0b' }}>
                        ⚠️ No device IP set — go to <strong style={{ margin: '0 3px' }}>Settings → IoT Device</strong> first
                    </DeviceHint>
                )}
            </ScanZone>

            {/* ── Manual fallback ── */}
            <Divider>or enter UID manually</Divider>

            <div>
                <label
                    style={{ fontSize: '12px', fontWeight: 600, color: '#4b5563', display: 'block', marginBottom: 6 }}
                >
                    RFID Card UID
                </label>
                <InputWrapper>
                    <InputIcon>
                        <CreditCardOutlined />
                    </InputIcon>
                    <ManualInput
                        placeholder="e.g. A1B2C3D4  (HEX UID from device serial monitor)"
                        value={manualUid}
                        onChange={(e) => setManualUid(e.target.value.toUpperCase())}
                        onKeyDown={(e) => e.key === 'Enter' && handleSubmit()}
                        disabled={scanning}
                    />
                </InputWrapper>
                <div style={{ fontSize: '11px', color: '#9ca3af', marginTop: 5 }}>
                    Can also read from Serial Monitor: look for{' '}
                    <code
                        style={{ background: '#f3f4f6', padding: '1px 5px', borderRadius: 4, fontSize: '11px' }}
                    >
                        RFID_MATCH:XXXXXXXX
                    </code>
                </div>
            </div>

            {/* ── Actions ── */}
            <ActionButtons>
                <CancelBtn onClick={onCancel} disabled={loading || scanning}>
                    Cancel
                </CancelBtn>
                <SaveBtn
                    onClick={handleSubmit}
                    disabled={loading || scanning || !manualUid.trim()}
                >
                    {loading ? (
                        <>
                            <LoadingOutlined />
                            Saving...
                        </>
                    ) : (
                        'Save Card'
                    )}
                </SaveBtn>
            </ActionButtons>
        </StyledModal>
    );
};

export default RfidRegisterModal;
